import { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { store } from "Store";
import { Row, Col, Card, Form } from "react-bootstrap";
import { TextButton } from "Components/Button";
import { ERROR_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import { PageInfo } from "Components/PageInformation/PageInformation";
import { sendSmsFactor, validateToken, submitMfa } from "./ForgotPasswordAxiosFuncs";
import { PATHS } from "Utils/PagePaths";

const ForgotPasswordMFA = () => {

  const { token } = useParams();
  const loginState = store.getState().loginInfo;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [mfaToken, setMfaToken] = useState("");
  // error messages display on top 
  const [globalErrorList, setGlobalErrorList] = useState([]);
  const [disableSendCode, setDisableSendCode] = useState(false);
  const [showSendCodeMsg, setShowSendCodeMsg] = useState(false);
  const [disableMfaTokenField, setDisableMfaTokenField] = useState(true);
  const tokenInputRef = useRef();


  // handle six digit mfa token field change
  const handleMfaChange = (e) => {

    let value = e.target.value;
    const regex = /^[0-9\b]+$/;

    //console.log("test mfa code", regex.test(value));

    if (value === '' || regex.test(value)) {
      setMfaToken(e.target.value);
    }
  }

  const handleSendCode = async () => {
    setDisableSendCode(true);
    const res = await sendSmsFactor(token);

    //console.log("handleSendCode()", res);

    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.globalErrorMessageList);
      setDisableSendCode(false);
    } else {
      setTimeout(() => {
        setDisableSendCode(false);
      }, 30000);
      setShowSendCodeMsg(true);
      setDisableMfaTokenField(false);
      setTimeout(() => {
        tokenInputRef.current.focus();
      }, 500);
    }
  }


  // init load to validate the token in the URL from the email
  const initLoad = async (token) => {
    // the Forgot Password MFA can only be operated when the user is not login!
    if (loginState.token) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.GENERAL_ERROR.title,
          msg: "Please sign out to reset password.",
          showContactHelp: false
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        }
      });
      //stay on the  page
      setIsLoading(true);
      return;
    }

    const res = await validateToken(token)
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });

    //console.log("initLoad() forgotpassword", res);
    if (res && res.data?.status !== 1) {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: res.data.globalErrorMessageList,
          showContactHelp: false
        },
        redirect: {
          redirect: true,
          redirectFirst: true,
          path: PATHS.public['Sign In'],
          data: {}
        }
      });
      return;
    }
    if (res && res.data?.status === 1) {
      // forwardPage:1: Reset Password; 2: Setup MFA;   3: Verify MFA 
      if (res.data.data?.forwardPage === 3) {
        setIsLoading(false);
      }

      if (res.data.data?.forwardPage === 1) {
        setIsLoading(false);
        navigate("/token/" + token + "/2", { state: { isForward: true } });
      }
    }
  }

  // api to verify the MFA token
  const verifyToken = async () => {
    // clear error
    setGlobalErrorList([]);
    setShowSendCodeMsg(false);

    const res = await submitMfa(token, mfaToken);
    //console.log("verifyToken", res);
    if (res && res.data.status !== 1) {
      setGlobalErrorList(res.data.globalErrorMessageList);
    } else {
      // go to reset password page
      navigate("/token/" + token + "/2", { state: { isForward: true } });
    }
  }

  // init
  useEffect(() => {
    initLoad(token);
  }, []);

  const cancelMfa = () => {
    navigate(PATHS.public['Sign In']);
  }

  return (
    <>
      {isLoading ? null :
        <>
          <Row>
            <Col xl={10} lg={11} md={11} sm={12}>
              <Card className="p-4">
                <Card.Body className="text-start">
                  <h1 id="mainContent" className="text-start mb-3 blue bold h1-form-title">
                    Two-Factor Authentication
                  </h1>
                  {showSendCodeMsg ?
                    <>
                      <PageInfo className="mb-3" type="info" message={"A code has been sent to your registered cell phone. Please enter that code in the field below. You may send a new code after 30 seconds if needed."}></PageInfo>
                      <br />
                    </>
                    : null}
                  {globalErrorList.map((item, index) =>
                    <>
                      <PageInfo key={"errors_" + index} type="error" message={item}></PageInfo>
                      <br />
                    </>
                  )}
                  <p>
                    Please click the <span style={{ color: "#1673B6", fontWeight: "600" }}>Send Code</span> button to receive your challenge code on your registered mobile device. Please enter the code in the text box and click the <span style={{ color: "#1673B6", fontWeight: "600" }}>Verify</span> button.
                  </p>
                  <Form >
                    <Row className="mb-4">
                      <Col sm="5" className="pt-2 mfaLabel">1. Click to send code to your registered mobile device</Col>
                      <Col>
                        <TextButton
                          className="mt-1"
                          disabled={disableSendCode}
                          variant="normal"
                          onClick={handleSendCode}
                          content={"Send Code"}
                        >
                        </TextButton>
                      </Col>
                    </Row >
                    <Form.Group as={Row} className="mb-3" controlId="mfaToken">
                      <Form.Label column sm="5" className="mfaLabel">
                        2. Enter the six digit code
                      </Form.Label>
                      <Col sm={3}>
                        <Form.Control
                          ref={tokenInputRef}
                          disabled={disableMfaTokenField}
                          type="text"
                          maxlength={6}
                          value={mfaToken}
                          onChange={handleMfaChange}
                        />
                      </Col>
                    </Form.Group>
                    <Row className="mb-3 pt-1">
                      <Col sm="5" className="pt-2 mfaLabel">3. Click Verify to confirm</Col>
                      <Col className="mt-1">
                        <TextButton
                          className="w-10"
                          variant="normal"
                          disabled={mfaToken.length !== 6}
                          onClick={verifyToken}
                          content={"Verify"}
                        >
                        </TextButton>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <TextButton
                          className="w-10"
                          variant="outline"
                          onClick={cancelMfa}
                          content={"Cancel"}
                        >
                        </TextButton>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      }
    </>
  )
}

export default ForgotPasswordMFA;